/**
 * ApprovalSignOff.jsx
 * ====================
 * Reusable sign-off display block for all approval touch-points in SFM.
 *
 * Shows one of three states:
 *   - Approved: green block with name, position, date
 *   - Rejected: red block with name, position, date, and reason
 *   - Pending:  amber block "Awaiting approval"
 *   - None:     renders nothing (status not an approval state)
 *
 * Props:
 *   status          string   — current record status (checked against approvedStatuses/rejectedStatuses)
 *   approvedBy      string   — full name of approver (approved_by_name field)
 *   approvedPosition string  — role title of approver (approved_by_position field)
 *   approvedAt      string   — ISO timestamp (approved_at field)
 *   rejectedBy      string   — full name of rejector (rejected_by_name field)
 *   rejectedPosition string  — role title (rejected_by_position field)
 *   rejectedAt      string   — ISO timestamp (rejected_at field)
 *   rejectionReason string   — reason text (notes or rejection_reason field)
 *   pendingStatuses string[] — statuses that show amber "Awaiting approval" block (default: ['pending', 'in_review'])
 *   approvedStatuses string[]— statuses that show green approved block (default: ['approved', 'released', 'active', 'approved_for_avl', 'closed'])
 *   rejectedStatuses string[]— statuses that show red rejected block (default: ['rejected', 'suspended', 'removed'])
 *   label           string   — action label: "Approved", "Released", "Activated", etc. (default: "Approved")
 *   sx              object   — additional MUI sx styles for the outer Box
 *
 * Usage examples:
 *
 *   // Task approval
 *   <ApprovalSignOff
 *     status={task.status}
 *     approvedBy={task.approved_by_name}
 *     approvedPosition={task.approved_by_position}
 *     approvedAt={task.approved_at}
 *     rejectedBy={task.rejected_by_name}
 *     rejectedPosition={task.rejected_by_position}
 *     rejectedAt={task.rejected_at}
 *     rejectionReason={task.notes}
 *   />
 *
 *   // Heat release
 *   <ApprovalSignOff
 *     label="Released"
 *     status={heat.status}
 *     approvedBy={heat.released_by_name}
 *     approvedPosition={heat.released_by_position}
 *     approvedAt={heat.released_at}
 *     approvedStatuses={['released']}
 *     pendingStatuses={['quarantine']}
 *   />
 */

import React from "react";
import { Box, Typography, Chip } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon     from "@mui/icons-material/CancelOutlined";
import HourglassEmptyIcon     from "@mui/icons-material/HourglassEmpty";
import { format, parseISO, isValid } from "date-fns";

// ── Default status sets ───────────────────────────────────────────────────────
const DEFAULT_APPROVED = ["approved", "released", "active", "approved_for_avl", "closed", "verified"];
const DEFAULT_REJECTED = ["rejected", "suspended", "removed", "revoked"];
const DEFAULT_PENDING  = ["pending", "in_review", "quarantine", "draft", "submitted"];

// ── Date formatter ────────────────────────────────────────────────────────────
const fmtDate = (raw) => {
  if (!raw) return "—";
  try {
    const d = typeof raw === "string" ? parseISO(raw) : new Date(raw);
    return isValid(d) ? format(d, "MMM d, yyyy  h:mm a") : raw;
  } catch {
    return String(raw);
  }
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function ApprovalSignOff({
  status,
  approvedBy,
  approvedPosition,
  approvedAt,
  rejectedBy,
  rejectedPosition,
  rejectedAt,
  rejectionReason,
  pendingStatuses  = DEFAULT_PENDING,
  approvedStatuses = DEFAULT_APPROVED,
  rejectedStatuses = DEFAULT_REJECTED,
  label            = "Approved",
  sx               = {},
}) {
  const s = (status || "").toLowerCase();
  const isApproved = approvedStatuses.map(x => x.toLowerCase()).includes(s);
  const isRejected = rejectedStatuses.map(x => x.toLowerCase()).includes(s);
  const isPending  = pendingStatuses.map(x => x.toLowerCase()).includes(s);

  // ── Approved ───────────────────────────────────────────────────────────────
  if (isApproved && (approvedBy || approvedAt)) {
    return (
      <Box sx={{
        display: "flex", alignItems: "flex-start", gap: 1.5,
        p: 1.5, borderRadius: 1,
        bgcolor: "success.lighter", border: "1px solid", borderColor: "success.light",
        ...sx,
      }}>
        <CheckCircleOutlineIcon sx={{ color: "success.main", mt: 0.2, flexShrink: 0 }} />
        <Box>
          <Typography variant="subtitle2" color="success.dark" gutterBottom={false}>
            {label} by: {approvedBy || "—"}, {approvedPosition || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fmtDate(approvedAt)}
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── Rejected / Suspended / Removed ────────────────────────────────────────
  if (isRejected) {
    const byName  = rejectedBy       || approvedBy       || "—";
    const byPos   = rejectedPosition || approvedPosition  || "—";
    const byAt    = rejectedAt       || approvedAt;
    const reason  = rejectionReason;
    return (
      <Box sx={{
        display: "flex", alignItems: "flex-start", gap: 1.5,
        p: 1.5, borderRadius: 1,
        bgcolor: "error.lighter", border: "1px solid", borderColor: "error.light",
        ...sx,
      }}>
        <CancelOutlinedIcon sx={{ color: "error.main", mt: 0.2, flexShrink: 0 }} />
        <Box>
          <Typography variant="subtitle2" color="error.dark" gutterBottom={false}>
            {label === "Approved" ? "Rejected" : label} by: {byName}, {byPos}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {fmtDate(byAt)}
          </Typography>
          {reason && (
            <Typography variant="caption" color="error.dark" display="block" sx={{ mt: 0.5 }}>
              Reason: {reason}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // ── Pending / Awaiting ─────────────────────────────────────────────────────
  if (isPending) {
    return (
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        p: 1.5, borderRadius: 1,
        bgcolor: "warning.lighter", border: "1px solid", borderColor: "warning.light",
        ...sx,
      }}>
        <HourglassEmptyIcon sx={{ color: "warning.main", flexShrink: 0 }} />
        <Typography variant="subtitle2" color="warning.dark">
          Awaiting {label.toLowerCase()}
        </Typography>
      </Box>
    );
  }

  // No sign-off state matches — render nothing
  return null;
}
